import { Typography } from '@mui/material'
import Grid from '@mui/material/Grid'
import { ColStack } from 'components/atoms/container/InCardStack'
import AssessmentLanding from 'components/molecules/assessments/AssessmentLanding'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getConstant } from 'services/constantServices'
import {
  ASSESSMENT_URL,
  ASSESSMENT_RECIPIENTS_URL,
  ASSESSMENT_RECIPIENT_STATUSES,
  CONSTANTS_OPTIONS
} from 'services/constants'
import { getGoalInfoById } from 'utils/getSupportingInfo'
import { postRequest, patchRequest } from 'utils/api'
import OverlayLoading from 'components/atoms/OverlayLoading'
import NotificationSnackBar from 'components/molecules/notificationOverlay/NotificationSnackBar'
import NavigateButtons from 'components/molecules/onboarding/NavigateButtons'
import {
  AssessmentLikertScale,
  AssessmentProgressBar,
  AssessmentNAButton
} from 'components/molecules/assessments/AssessmentMolecules'

const AssessmentCard = ({
  goalId,
  selfAssessment,
  assessmentSenderName, // For others assessment
  assessmentRecipientId, // For others assessment
  questions,
  submittedCallback,
  previousStepCallback
}) => {
  const { t } = useTranslation()
  const [started, setStarted] = useState(false)
  const [goalData, setGoalData] = useState({})
  const [options, setOptions] = useState([])
  const [answers, setAnswers] = useState([])
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [naOption, setNAOption] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const questionField = selfAssessment ? 'selfQuestion' : 'othersQuestion'

  useEffect(() => {
    const fetchData = async () => {
      const evaluateOptions = await getConstant(
        CONSTANTS_OPTIONS.EVALUATION_OPTIONS
      )
      if (evaluateOptions) {
        setOptions(evaluateOptions.filter(o => o.value !== 0))
        setNAOption(evaluateOptions.find(o => o.value === 0))
      }
      const goal = await getGoalInfoById(goalId)
      setGoalData(goal)
    }
    if (goalId) {
      fetchData()
    }
  }, [goalId])

  useEffect(() => {
    if (questions.length) {
      setAnswers(Array(questions.length).fill(null))
    }
  }, [questions])

  const handleSubmit = async () => {
    setLoading(true)
    const selfScores = {}
    questions.forEach((q, index) => {
      selfScores[q.id] = parseInt(answers[index], 10)
    })

    let res
    if (selfAssessment) {
      res = await postRequest(ASSESSMENT_URL, {
        goalId,
        version: questions[0].version,
        selfScores
      })
    } else {
      res = await patchRequest(
        `${ASSESSMENT_RECIPIENTS_URL}/${assessmentRecipientId}`,
        {
          score: selfScores,
          status: ASSESSMENT_RECIPIENT_STATUSES.COMPLETED
        }
      )
    }
    if (res.status === 201 || res.status === 200) {
      submittedCallback(res.data.data)
    } else {
      setError(true)
    }
    setLoading(false)
  }

  let myTimeout = null
  const timeout = 500
  const updateAnswer = (idx, choice, moveToNext = true) => {
    const newAnswers = [...answers]
    newAnswers[idx] = choice
    setAnswers(newAnswers)
    myTimeout = setTimeout(() => {
      if (moveToNext) {
        handleNext()
      }
    }, timeout)
    return () => clearTimeout(myTimeout)
  }

  const answeredPercent = () =>
    answers.filter(x => x !== null).length / questions.length

  const handleNext = (submit = false) => {
    if (!started) {
      setStarted(true)
    } else if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(prev => prev + 1)
    } else if (submit) {
      handleSubmit()
    }
  }

  const checkNextDisabled = () => {
    if (!started) return false
    if (currentQuestion < questions.length - 1) {
      return (
        answers[currentQuestion] === null ||
        answers[currentQuestion] === undefined
      )
    }
    return answeredPercent() < 1
  }

  const currentNASelected = () => answers[currentQuestion] === naOption?.value

  const handleNAClick = () => {
    const updatedValue = currentNASelected() ? null : naOption.value
    const moveToNext =
      currentQuestion < questions.length - 1 &&
      !currentNASelected() &&
      answers[currentQuestion + 1] === null
    updateAnswer(currentQuestion, updatedValue, moveToNext)
  }
  const onBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1)
    } else if (previousStepCallback) {
      previousStepCallback()
    }
  }
  return (
    <ColStack spacing={16}>
      {!started ? (
        <AssessmentLanding
          selfAssessment={selfAssessment}
          goalName={goalData.leadershipPathName}
          handleBegin={() => setStarted(true)}
          questionCount={questions.length}
          assessmentSenderName={assessmentSenderName}
        />
      ) : (
        <ColStack spacing={16} data-testid='question-body'>
          <AssessmentProgressBar
            percentage={answeredPercent()}
            m={currentQuestion + 1}
            n={questions.length}
          />
          {questions.map(
            (q, index) =>
              index === currentQuestion && (
                <ColStack spacing={16} key={`assessment-question-${q.id}`}>
                  <Typography variant='h2'>{q[questionField]}</Typography>
                  <AssessmentLikertScale
                    id={index + 1}
                    onChange={v => updateAnswer(index, v)}
                    initialValue={answers[index]}
                    options={options}
                  />
                </ColStack>
              )
          )}
          <AssessmentNAButton
            selected={currentNASelected()}
            handleNAClick={handleNAClick}
          />
        </ColStack>
      )}

      <Grid position='sticky'>
        <NavigateButtons
          showBack={!!previousStepCallback || currentQuestion > 0}
          onBack={onBack}
          onNext={() => handleNext(true)}
          nextLabel={
            !started
              ? selfAssessment
                ? t('assessmentTimeline.beginSelfAssessment')
                : t('assessmentForOthers.startUserAssessment', {
                    assessmentSenderName: assessmentSenderName.split(' ')[0]
                  })
              : currentQuestion < questions.length - 1
                ? null
                : selfAssessment
                  ? t('assessments.submitGetResults')
                  : t('assessments.submit')
          }
          nextProps={{
            disabled: checkNextDisabled(),
            'data-testid':
              currentQuestion < questions.length - 1
                ? 'assessment-next-button'
                : 'submit-button'
          }}
          backProps={{
            'data-testid': 'assessment-back-button'
          }}
        />
      </Grid>

      {loading && <OverlayLoading open={loading} />}
      <NotificationSnackBar
        error={error}
        open={error}
        setOpen={setError}
        autoHideDuration={5000}
      />
    </ColStack>
  )
}

export default AssessmentCard
